/**
 * BucketSettingsCard - Component for displaying and editing bucket calibration settings
 */

import './RigCalibration.css';
import { CALIBRATION_SLOTS, VALIDATION_LIMITS } from './constants';
import { BucketSettingsCardProps, CalibrationValues } from './types';
import { useSampleSourceCatalog } from '@/hooks/useSampleSourceCatalog';
import { useLanguage } from '@/i18n/LanguageContext';
import { calibrationSlotDisplayName } from '@/utils/sampleSources';
import { LocalizedNumberInput } from '@/components/LocalizedNumberInput/LocalizedNumberInput';

import {
  Card,
  Text,
  Title,
  Alert,
  Group,
  Stack,
  Divider,
  Table,
  Button,
} from '@mantine/core';

import IconAlertCircle from '@tabler/icons-react/IconAlertCircle';
import IconCheck from '@tabler/icons-react/IconCheck';
import IconDeviceFloppy from '@tabler/icons-react/IconDeviceFloppy';

export default function BucketSettingsCard({
  calibrationValues,
  validationErrors,
  saving,
  saveSuccess,
  hasUnsavedChanges,
  onCalibrationValueChange,
  onSave,
  onSaveSuccessClose,
  onValidationErrorsClose,
}: BucketSettingsCardProps) {
  const { t, language } = useLanguage();
  const { sampleSources } = useSampleSourceCatalog();
  const handleValueChange = (field: keyof CalibrationValues, value: number) => {
    onCalibrationValueChange(field, value);
  };

  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  return (
    <Card withBorder padding="md" radius="md" className="rc-card">
      <Title order={2} mb="xs" className="rc-sectionTitle">
        {t('calibration.factors')}
      </Title>

      {saveSuccess && (
        <Alert
          icon={<IconCheck size="1rem" />}
          title={t('common.success')}
          color="green"
          mb="md"
          withCloseButton
          onClose={onSaveSuccessClose}
          className="rc-alert"
        >
          {t('calibration.saved')}
        </Alert>
      )}

      {hasValidationErrors && (
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          title={t('calibration.validationError')}
          color="red"
          mb="md"
          withCloseButton
          onClose={onValidationErrorsClose}
          className="rc-alert"
        >
          <Stack gap="xs">
            {Object.values(validationErrors).map((error, index) => (
              <Text key={index} size="sm">
                {error}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Divider my="xs" className="rc-divider" />

      <Table
        striped
        highlightOnHover
        withTableBorder
        className="rc-table rc-calibrationFactorsTable"
      >
        <colgroup>
          <col className="rc-calibrationFactorsTable-sourceColumn" />
          <col className="rc-calibrationFactorsTable-valueColumn" />
          <col className="rc-calibrationFactorsTable-valueColumn" />
        </colgroup>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('results.sampleSource')}</Table.Th>
            <Table.Th>
              <Text fw={700}>
                {t('calibration.oilPercent')}
              </Text>
            </Table.Th>
            <Table.Th>
              <Text fw={700}>
                {t('calibration.waterPercent')}
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {CALIBRATION_SLOTS.map((source) => {
            const oilField = `${source.key}_oil_calibration` as keyof CalibrationValues;
            const waterField = `${source.key}_water_calibration` as keyof CalibrationValues;
            return (
              <Table.Tr key={source.key}>
                <Table.Td>
                  <Text
                    fw={600}
                    size="sm"
                    className="rc-calibrationFactorsTable-source"
                  >
                    {calibrationSlotDisplayName(
                      sampleSources,
                      source.key,
                      language
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <LocalizedNumberInput
                    value={calibrationValues[oilField]}
                    onChange={(val) => handleValueChange(oilField, typeof val === 'number' ? val : 0)}
                    min={VALIDATION_LIMITS.MIN_VALUE}
                    max={VALIDATION_LIMITS.MAX_VALUE}
                    step={0.01}
                    decimalScale={2}
                    size="sm"
                    error={!!validationErrors[oilField]}
                  />
                </Table.Td>
                <Table.Td>
                  <LocalizedNumberInput
                    value={calibrationValues[waterField]}
                    onChange={(val) => handleValueChange(waterField, typeof val === 'number' ? val : 0)}
                    min={VALIDATION_LIMITS.MIN_VALUE}
                    max={VALIDATION_LIMITS.MAX_VALUE}
                    step={0.01}
                    decimalScale={2}
                    size="sm"
                    error={!!validationErrors[waterField]}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>

      <Group justify="center" mt="md">
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={onSave}
          loading={saving}
          disabled={saving || hasValidationErrors || !hasUnsavedChanges}
          size="sm"
          className="rc-button"
        >
          {t('common.save')}
        </Button>
      </Group>
    </Card>
  );
}
